<template>
	<div>
		<v-form
			v-if="!isPollVoted"
			:class="{
				'poll-form px-4': true,
				'poll-form-event': !postId,
				'poll-form-event--session': !postId && isSessionsStyled,
			}"
			ref="form"
			v-model="valid"
			lazy-validation
			@submit.prevent="validateAndSubmit"
		>
			<p v-if="!postId" class="poll-form__event-title mb-3">{{ poll.poll_text }}</p>
			<v-checkbox
				v-for="answer in pollAnswers"
				:key="answer.poll_answer_id"
				class="poll-form__event-answer pa-0 ma-0 pb-3"
				:class="{ 'poll-form__event-answer--session': isSessionsStyled }"
				:label="answer.poll_answer"
				:value="answer.poll_answer_id"
				v-model="fields.answer"
				:ripple="false"
				hide-details
				:disabled="pollsLoading"
			/>

			<div
				:class="{ 'poll-form__custom-answer--white': isSessionsStyled }"
				class="poll-form__custom-answer-container"
				v-if="customAnswerAllowed"
			>
				<v-checkbox
					class="poll-form__answer pa-0 ma-0 pb-3"
					:class="{ 'poll-form__event-answer--session': isSessionsStyled }"
					v-model="fields.answer"
					label="Your answer:"
					value="custom"
					:ripple="false"
					hide-details
					:disabled="pollsLoading"
				/>
				<v-text-field
					class="poll-form__custom-answer pa-0 ma-0 mb-3 ml-2"
					v-model="fields.customAnswer"
					hide-details
					:disabled="pollsLoading"
				/>
			</div>
			<div style="position: relative">
				<p v-if="isDirty || isEventVoteDirty" class="poll-form__error">
					<span v-if="typeof isCheckboxesValid === 'string'">
						{{ isCheckboxesValid }}
					</span>
					<span v-else-if="!customAnswerIsValid"> Custom answer cannot be empty </span>
				</p>
				<v-btn
					v-if="postId"
					class="poll-form__submit-btn elevation-0 mt-4"
					:class="{ 'poll-form__submit-btn-disabled': !isSubmitEnabled }"
					@click="handleSubmit"
					:loading="isPollVoting"
					:ripple="false"
				>
					Vote
				</v-btn>
			</div>
		</v-form>
		<v-card
			v-else-if="poll.display_poll"
			:class="{ 'poll-result px-4 elevation-0': true, 'poll-result-event': !postId }"
		>
			<p v-if="!postId" class="poll-form__event-title mb-3">
				{{ poll.poll_text }}
			</p>
			<div v-for="answer in poll.pollsAnswers" :key="answer.poll_answer_id">
				<div style="background-color: #ffffff !important" class="poll-result__answer-scale">
					<div
						class="poll-result__answer-scale-bar"
						:style="getScaleBarStyle(answer)"
					></div>
					<div class="poll-result__answer-scale__answer">
						{{ answer.is_custom ? 'Custom answer' : answer.poll_answer }}
					</div>
					<div
						:class="{
							'poll-result__answer-scale__percentage': true,
							'poll-result__percentage--bold': isMaxPercentage(answer),
						}"
					>
						{{ Math.round(calcPercentage(answer)) }} %
					</div>
				</div>
			</div>
		</v-card>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import Dwh from '@/mixins/Dwh'

const AT_LEAST_TYPE = 0
const AT_MOST_TYPE = 1
const EXACTLY_TYPE = 2

export default {
	mixins: [Form, Dwh],
	props: {
		poll: {
			type: Object,
			default: () => ({}),
		},
		postId: {
			type: Number,
			default: null,
		},
		post: {
			type: Object,
			default: () => ({}),
		},
		answers: {
			type: Array,
			default: () => [],
		},
		isEventVoteDirty: {
			type: Boolean,
			default: false,
		},
		pollsLoading: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			fields: {
				answer: [],
				customAnswer: '',
			},
			isDirty: false,
		}
	},
	computed: {
		...mapGetters('settings', ['styles']),
		...mapGetters('post', ['isPollVoting']),
		isSessionsStyled() {
			return this.$route.name === 'event-session-by-id'
		},
		pollAnswers() {
			return this.poll.pollsAnswers.filter((ans) => ans.poll_answer !== 'Custom answer')
		},

		isPollVoted() {
			return this.poll.vote
		},
		customAnswerAllowed() {
			return this.poll.allow_custom_answer
		},
		isSubmitEnabled() {
			return Array.isArray(this.fields.answer)
				? this.fields.answer.length
				: this.fields.answer
		},
		answersCount() {
			return this.poll.pollsAnswers.reduce((acc, item) => acc + item.poll_answer_count, 0)
		},
		customAnswerIsValid() {
			if (Array.isArray(this.fields.answer)) {
				return this.fields.answer.some((item) => item === 'custom')
					? !!this.fields.customAnswer
					: true
			} else {
				return this.fields.answer === 'custom' ? !!this.fields.customAnswer : true
			}
		},
		isCheckboxesValid() {
			if (!Array.isArray(this.fields.answer)) {
				return !!this.fields.answer || 'Answer required'
			} else {
				switch (this.poll.require_answer_option) {
					case AT_LEAST_TYPE: {
						return (
							this.poll.num_of_answers <= this.fields.answer.length ||
							`At least ${this.poll.num_of_answers} variants required`
						)
					}
					case AT_MOST_TYPE: {
						return (
							this.poll.num_of_answers >= this.fields.answer.length ||
							`At most ${this.poll.num_of_answers} variants required`
						)
					}
					case EXACTLY_TYPE: {
						return (
							this.poll.num_of_answers === this.fields.answer.length ||
							`Exactly ${this.poll.num_of_answers} variants required`
						)
					}
					default: {
						return null
					}
				}
			}
		},
		maxPercentage() {
			if (!this.answersCount) return 0
			const all = this.poll.pollsAnswers.map((a) => this.calcPercentage(a))
			return Math.max(...all)
		},
	},
	watch: {
		'fields.answer': {
			handler(newValue) {
				if (!this.postId) {
					const answer = {
						poll_id: this.poll.poll_id,
						answer: newValue,
					}
					if (
						(Array.isArray(newValue) && newValue.includes('custom')) ||
						newValue === 'custom'
					) {
						answer.customAnswer = this.fields.customAnswer
					}
					answer.error =
						typeof this.isCheckboxesValid === 'string'
							? this.isCheckboxesValid
							: this.customAnswerIsValid
							? null
							: 'Custom answer cannot be empty'
					this.$emit('answerChange', answer)
				}
			},
		},

		'fields.customAnswer': {
			handler(newValue) {
				if (!this.postId) {
					const answer = {
						poll_id: this.poll.poll_id,
						answer: this.fields.answer,
					}
					if (
						(Array.isArray(this.fields.answer) &&
							this.fields.answer.includes('custom')) ||
						this.fields.answer === 'custom'
					) {
						answer.customAnswer = newValue
					}
					answer.error = newValue.length ? null : 'Custom answer cannot be empty'
					this.$emit('answerChange', answer)
				}
			},
		},
	},
	methods: {
		...mapActions('post', ['votePoll']),
		getScaleBarStyle(answer) {
			const width = this.calcPercentage(answer) + '%'
			const backgroundColor =
				answer.selected === 1 ? 'rgba(79, 137, 213, 0.3)' : 'rgba(200, 200, 200, 0.3)'
			const borderRadius =
				Math.round(this.calcPercentage(answer)) === 100 ? '8px' : '8px 0 0 8px'

			return {
				width,
				backgroundColor,
				borderRadius,
			}
		},
		isMaxPercentage(answer) {
			const calcValue = this.calcPercentage(answer)
			const maxValue = this.maxPercentage
			return Math.abs(calcValue - maxValue) < 0.1
		},
		calcPercentage(answer) {
			const percentage = (answer.poll_answer_count / this.answersCount) * 100
			if (Number.isInteger(percentage)) {
				return percentage
			} else {
				return percentage.toFixed(1)
			}
		},
	},
	mounted() {
		const option = this.poll.require_answer_option
		if (
			this.poll.num_of_answers === 1 &&
			(option === AT_MOST_TYPE || option === EXACTLY_TYPE)
		) {
			this.fields.answer = null
		} else {
			this.fields.answer = []
		}
	},
}
</script>

<style lang="scss">
.poll-form {
	padding-top: 10px;
	font-family: 'Inter', sans-serif;

	div {
		width: auto !important;
		max-width: none !important;
	}
	&__custom-answer-container {
		display: flex;
		align-items: center;
		background-color: #f3f3f6;
		padding: 12px 12px 0 12px;
		border-radius: 8px;
	}
	&__custom-answer--white {
		background-color: #ffffff !important;
	}

	&-event {
		background-color: #ffffff;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
		margin-bottom: 12px;
		padding: 16px;
	}
	&-event--session {
		background-color: #f3f3f6 !important;
	}

	&__event-title {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		color: var(--accent-color);
	}

	&__event-answer {
		color: #002855 !important;
		.v-input__control {
			background-color: transparent !important;
		}
		.v-label {
			color: var(--accent-color) !important;
		}

		.v-input__slot {
			background-color: #f3f3f6 !important;
			padding: 12px;
			border-radius: 8px;
		}
		&--session {
			.v-input__slot {
				background-color: #ffffff !important;
			}
		}
		i {
			font-size: 20px !important;
			color: #e2e2e2 !important;
		}
	}

	&__answer {
		label {
			font-family: Inter, sans-serif !important;
			font-weight: 500;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--accent-color) !important;
		}
	}

	&__custom-answer {
		border-bottom: 1px solid #e2e2e2;

		input {
			padding: 0 !important;
		}

		.v-input__slot::after {
			border-color: var(--accent-color) !important;
		}
	}
	.v-input--is-label-active {
		i {
			color: var(--accent-color) !important;
		}
	}

	&__submit-btn {
		width: 100%;
		height: 50px !important;
		border-radius: 8px;
		font-size: 18px !important;
		line-height: 22px;
		font-weight: 700;
		text-transform: capitalize;
		background-color: var(--button-main-background-color) !important;
		color: var(--button-main-color) !important;

		&-disabled {
			opacity: 0.5;
			cursor: default;
		}

		.v-progress-circular {
			width: 23px !important;
		}
	}

	&__error {
		position: absolute;
		top: -8px;
		left: 50%;
		transform: translateX(-50%);
		font-size: 12px;
		color: red;
		margin: 0;
	}
}

.poll-result {
	padding-top: 10px;
	font-family: 'Inter', sans-serif;
	&-event {
		padding: 16px;
		background-color: #f3f3f6 !important;
		margin-bottom: 10px;
		border-radius: 10px !important;
		border: 1px solid #e2e2e2 !important;

		.v-progress-linear__buffer {
			background-color: #fff !important;
		}
	}

	&__percentage,
	&__answer {
		display: inline-block;
		font-size: 14px;
		font-weight: 500;
		line-height: 17px;
		color: var(--accent-color);
		min-width: 37px;
	}

	&__percentage {
		white-space: nowrap;
	}

	&__answer {
		word-break: break-word;
	}
}
</style>
